import { useCallback, useEffect, useState } from 'react';
import isEmail from 'validator/lib/isEmail';

import { toCamelCase } from '@autopass-web/domain';
import useInterval from '@autopass-web/domain/hooks/use-interval';
import {
  Button,
  FormInput,
  MochiCheckbox,
} from '@autopass-web/mochi-component';

import { ActionButton, Block, PageLayout } from '../components/UI';
import { environment } from '../environments/environment';
import { analyticsService } from '../services/amplitude';
import storage from '../services/storage';
import { useAuth } from '../services/use-auth';
import { useOtpRequest } from '../services/use-otp-request';
import { useRefetchUserMe } from '../services/use-user-me';
import { useUserMeUpdate } from '../services/use-user-me-update';
import { useToast } from '../store/toast';
import { userEditStore } from '../store/user-store';

export function LoginPage() {
  const OTP_TIME_UP = 60;
  const refetch = useRefetchUserMe();
  const toast = useToast();

  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [marketingEmailOptedIn, setMarketingEmailOptedIn] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [startOtp, setStartOtp] = useState(false);
  const [otpCounter, setOtpCounter] = useState(0);

  const { requestOtpMutateAsync, requestOtpStatus } = useOtpRequest();
  const { authMutateAsync, authStatus } = useAuth();
  const { updateUserMeMutateAsync, updateUserMeStatus } = useUserMeUpdate();
  const handleRequestOtp = useCallback(async () => {
    setOtpCounter(0);
    setStartOtp(true);
    setMobileError('');
    setEmailError('');
    try {
      await requestOtpMutateAsync({ mobile, email });
    } catch (e) {
      const status = e.response.status;
      if (status === 422) {
        toast.onToastError(e.response.data.error.message);
      } else {
        toast.onToastError('網路連線發生錯誤，請稍後再試。');
      }
      setOtpCounter(0);
      setStartOtp(false);
    }
  }, [requestOtpMutateAsync, mobile, email, toast]);

  const handleAuth = useCallback(async () => {
    if (email.length === 0) {
      setEmailError('E-mail 格式不正確');
      return;
    } else {
      setEmailError('');
    }
    if (mobile.length === 0) {
      setMobileError('手機號碼格式不正確');
      return;
    } else {
      setMobileError('');
    }
    try {
      const { data } = await authMutateAsync({
        mobile,
        email,
        code: otp,
        marketingEmailOptedIn,
      });
      storage.saveAuth(toCamelCase(data.data));
      await updateUserMeMutateAsync({
        agreeTermsFfTaipeiCityParkinglotService: true,
      });

      await refetch();
    } catch (e) {
      const status = e.response.status;
      if (status === 422) {
        toast.onToastError(e.response.data.error.message);
      } else {
        toast.onToastError('網路連線發生錯誤，請稍後再試。');
      }
    }
  }, [
    email,
    mobile,
    authMutateAsync,
    otp,
    marketingEmailOptedIn,
    updateUserMeMutateAsync,
    refetch,
    toast,
  ]);

  useInterval(
    () => {
      if (otpCounter >= OTP_TIME_UP) {
        setOtpCounter(0);
        setStartOtp(false);
      } else {
        setOtpCounter(otpCounter + 1);
      }
    },
    startOtp ? 1000 : null,
  );

  useEffect(() => {
    analyticsService.memberInfoPageview({
      domain: window.location.hostname,
      path: window.location.pathname,
      query_string: window.location.search,
    });
    // only execute once on mounting
  }, []);

  useEffect(() => {
    userEditStore.reset();
  }, []);

  const actions = (
    <div className="w-full flex flex-col items-center">
      <div className="text-white mb-5 text-[14px] font-[200]">
        <span>開始使用即同意</span>
        <a
          href={environment.termsUrl}
          onClick={analyticsService.memberInfoTermsClick}
          style={{ color: '#007aff' }}
        >
          服務條款
        </a>
        {` / `}
        <a
          href={environment.privacyUrl}
          onClick={analyticsService.memberInfoPrivacyClick}
          style={{ color: '#007aff' }}
        >
          隱私權政策
        </a>
      </div>

      <ActionButton
        isLoading={authStatus === 'pending' || updateUserMeStatus === 'pending'}
        onClick={() => {
          analyticsService.memberInfoNextStepClick();
          if (otp.match(/[0-9]{6}/)) {
            handleAuth();
          } else {
            setOtpError('簡訊驗證碼格式不正確');
          }
        }}
      >
        下一步
      </ActionButton>
    </div>
  );

  return (
    <PageLayout title={'車麻吉邀請您成為麻吉！'} bottom={actions}>
      <Block title="會員資料">
        <FormInput
          appearance={'grey'}
          placeholder="輸入會員 Email 信箱"
          value={email}
          disabled={startOtp}
          errorMessage={emailError}
          onTextChange={(text) => setEmail(text)}
          onFocus={() => {
            analyticsService.memberInfoEmailInputClick();
          }}
        />
        <div className="w-full flex flex-row gap-x-2">
          <FormInput
            digits="[0-9]"
            appearance={'grey'}
            placeholder="輸入會員手機號碼"
            value={mobile}
            errorMessage={mobileError}
            disabled={startOtp}
            maxLength={10}
            onTextChange={(text) => setMobile(text)}
            onFocus={() => {
              analyticsService.memberInfoPhoneInputClick();
            }}
          />
          <Button
            className="w-36"
            loading={requestOtpStatus === 'pending'}
            disabled={startOtp}
            onClick={() => {
              analyticsService.memberInfoSendOtpClick();
              if (!mobile.match(/09[0-9]{8}/)) {
                setMobileError('手機號碼格式不正確');
              } else if (!isEmail(email)) {
                setEmailError('E-mail 格式不正確');
              } else {
                handleRequestOtp();
              }
            }}
          >
            {startOtp ? `${OTP_TIME_UP - otpCounter}秒` : '傳送驗證碼'}
          </Button>
        </div>
        <FormInput
          appearance={'grey'}
          placeholder="輸入驗證碼"
          value={otp}
          maxLength={6}
          errorMessage={otpError}
          digits="[0-9]"
          onFocus={() => {
            analyticsService.memberInfoEnterOtpClick();
          }}
          onTextChange={(text) => setOtp(text)}
        />
        <div className={'flex gap-2 justify-start items-start'}>
          <MochiCheckbox
            checked={marketingEmailOptedIn}
            value={'marketingEmailOptedIn'}
            onChange={() => {
              setMarketingEmailOptedIn(!marketingEmailOptedIn);
              analyticsService.memberInfoMarketingOptedIn(
                !marketingEmailOptedIn,
              );
            }}
          />
          <div>
            <div>
              同意車麻吉使用我的電子郵件，以寄送行銷活動、會員優惠與最新資訊。您可隨時於信件中取消訂閱。
              （
              <a
                href={environment.marketingConsentUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  analyticsService.memberInfoMarketingConsentClick();
                }}
              >
                直接行銷同意條款
              </a>
              ）
            </div>
          </div>
        </div>
      </Block>
    </PageLayout>
  );
}
